import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { Printer, ArrowLeft, AlertTriangle, XCircle, CheckCircle2 } from "lucide-react";

function money(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

export default function TechnicianScorecardPage() {
  const { techId } = useParams();
  const [tech, setTech] = useState(null);
  const [declines, setDeclines] = useState([]);
  const [appts, setAppts] = useState([]);

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get("/technicians").then((r) => r.data),
      api.get(`/declines?technician_id=${techId}`).then((r) => r.data),
      api.get("/appointments").then((r) => r.data),
    ])
      .then(([techList, decList, apptList]) => {
        if (!live) return;
        setTech(techList.find((t) => t.id === techId) || null);
        setDeclines(decList);
        setAppts(apptList.filter((a) => decList.some((d) => d.id === a.decline_id)));
      })
      .catch(() => toast.error("Could not load scorecard"));
    return () => { live = false; };
  }, [techId]);

  const kpis = useMemo(() => {
    const total = declines.length;
    // Yellow items priced $150, red $400 in the MPI handoff — infer counts back
    let yellow = 0, red = 0;
    declines.forEach((d) => {
      (d.service_items || []).forEach((s) => {
        if (s.price === 400) red++;
        else if (s.price === 150) yellow++;
      });
    });
    const booked = declines.filter((d) => ["scheduled", "called_live"].includes(d.status)).length;
    const closedAppts = appts.filter((a) => a.status === "closed");
    const revenue = closedAppts.reduce((sum, a) => sum + (a.ro_total || 0), 0);
    return { total, yellow, red, booked, closedRO: closedAppts.length, revenue };
  }, [declines, appts]);

  const dot = tech?.color || "#059669";

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Technician Scorecard" />

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/technicians" className="font-mono-label text-muted-foreground hover:text-foreground inline-flex items-center gap-1 no-print" data-testid="tscorecard-back">
            <ArrowLeft className="w-3 h-3" strokeWidth={2} />
            All Technicians
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <div className="w-4 h-4 rounded-full ring-2 ring-background shadow" style={{ backgroundColor: dot }} />
            <h1 className="font-display text-4xl font-bold tracking-tighter" data-testid="tscorecard-name">
              {tech?.name || "Technician"}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Every MPI item this tech caught → texted to the customer → tracked through booking → closed RO.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-tscorecard-btn" className="no-print">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { label: "Inspections", val: kpis.total, testid: "kpi-total", Icon: CheckCircle2, color: "text-navy" },
          { label: "Yellow flags", val: kpis.yellow, testid: "kpi-yellow", Icon: AlertTriangle, color: "text-amber-600" },
          { label: "Red flags", val: kpis.red, testid: "kpi-red", Icon: XCircle, color: "text-red-600" },
          { label: "Booked back", val: kpis.booked, testid: "kpi-booked" },
          { label: "Closed ROs", val: kpis.closedRO, testid: "kpi-closed" },
          { label: "Revenue", val: money(kpis.revenue), testid: "kpi-revenue", accent: true },
        ].map((k) => (
          <Card key={k.label} className={`p-3 border-border ${k.accent ? "bg-emerald-50 border-emerald-300" : ""}`} data-testid={`tscorecard-${k.testid}`}>
            <div className="flex items-center gap-1.5 mb-1">
              {k.Icon && <k.Icon className={`w-3.5 h-3.5 ${k.color || "text-muted-foreground"}`} strokeWidth={2} />}
              <div className="font-mono-label text-muted-foreground text-[0.55rem]">{k.label}</div>
            </div>
            <div className="font-display font-black text-xl md:text-2xl tabular-nums tracking-tighter">
              {k.val}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-5 border-border">
        <div className="font-display font-bold text-lg tracking-tight mb-3">
          Recent MPI Findings · {declines.length} declines
        </div>
        {declines.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No MPI handoffs yet. Complete an inspection with yellow/red items and hit "Text to customer" — they'll show here.
          </div>
        ) : (
          <div className="space-y-1.5">
            {declines.slice(0, 30).map((d) => (
              <div key={d.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm" data-testid={`tscorecard-decline-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{d.customer_name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {d.vehicle} · {(d.service_items || []).length} item{(d.service_items || []).length === 1 ? "" : "s"}
                  </div>
                </div>
                <Badge variant="outline" className="font-mono-label text-[0.6rem]">
                  {d.status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </DashboardShell>
  );
}
